/**
 * WHICH PART OF THE PLANT A DEVICE BELONGS TO — derived from the graph, never typed.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE EXISTS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Jamshoro's as-built drawing is FIVE INDEPENDENT CLUSTERS, not one plant: 459.03 kW on
 * four inverters, then 102.96 on two, 77.22 on two, 185.76 on one and 45.76 on one. Each
 * cluster has its own AC busbar, its own energy meter and its own 400 V LV hookup
 * termination, and nothing in the drawing ties the five together. We model all ten
 * inverters as one flat set, so the structure is real, physical, and invisible.
 *
 * It matters for exactly one number, and it is worth being honest that the number is the
 * whole justification. Raw kWh cannot compare a 459 kW cluster with a 185 kW one; kWh per
 * kWp can, and the vendor portal's own August figures put cluster 3 at 129.3 against a site
 * average of 152.9 — 15 % down, on the cluster whose inverter carries a near-daily fault.
 * Alarm rollups over five buckets, four of which hold one or two inverters, are worth
 * nothing by comparison.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FIVE RULES, AND EACH ONE IS A DECISION THAT COULD HAVE GONE WRONG
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * 1. DERIVED FROM THE GRAPH, NOT STORED. There is no groups table and no tag on
 *    `site_devices`. A stored group would duplicate a fact the topology already holds, and
 *    the two would then be free to disagree — the same shape as the forked `device_models`
 *    rows and the duplicate `siteMismatch`. `Docs/02-domain/MULTI-INDUSTRY.md` §3 already
 *    ruled that a plant is a topology subgraph rather than a table; this is that ruling
 *    made executable rather than re-litigated.
 *
 * 2. THE ANCHOR IS WHAT MAKES A GROUP, and the anchor is a real piece of equipment: the
 *    node where branches COMBINE. Without a busbar, ten inverters are ten disconnected
 *    points and "cluster" is only a label somebody typed into a device name — which is
 *    precisely what it is in the vendor's portal, where C-1…C-5 are free text and no
 *    grouping object exists at all. With the busbar, the cluster is a fact the graph holds.
 *
 * 3. A NODE UPSTREAM OF TWO ANCHORS BELONGS TO NEITHER. This is not an edge case to be
 *    tidied away — it is how site-level equipment finds its correct home. The utility feed
 *    and the service meter serve every cluster, so they belong to none, and they are read
 *    ONCE for the whole site. Had they been placed in "all" groups instead, every rollup
 *    would have counted the service meter five times.
 *
 * 4. NO ANCHOR MEANS NO GROUPS, and no groups means no filter anywhere in the console.
 *    Both live sites are in exactly that state today: neither has a `busway`, `switchgear`,
 *    `dcbus` or `rpp` node, so this module returns nothing and every screen behaves exactly
 *    as it does now. The grouping switches itself on when the plant is drawn properly, with
 *    no further code and no migration.
 *
 * 5. ARCHETYPE NAMES THE GROUPING; IT NEVER DECIDES WHETHER THERE IS ONE. A generation
 *    plant's groups are clusters, an ICT plant's are distribution trains, and the mechanism
 *    is identical. Keying the FEATURE on archetype would make this an RMG-only thing, which
 *    is the failure that `plant-views.ts` exists to prevent one layer up.
 *
 * PURE. Constants and graph walking, no React, no I/O, no clock.
 */
import type { ArchetypeId } from '@/lib/compliance/types';
import { ARCHETYPE_IDS } from '@/lib/compliance/types';
import type { NodeKind, TopologyNode } from '@/lib/topology/model';

/**
 * The node kinds at which a plant SPLITS INTO PARTS — the bus a set of sources combines on.
 *
 * Chosen by what the equipment does, not by what it is called. A busbar, an LV panel, a DC
 * bus and a remote power panel all take several branches and present them as one; that is
 * the definition of a boundary between two parts of a plant.
 *
 * `pdu` is deliberately ABSENT and the omission is the interesting one. A PDU distributes
 * downstream rather than combining upstream, so anchoring on it would cut the plant at the
 * wrong place — at JAZZMSC5 it would make the shared PDU an anchor and file the two
 * redundant trains that feed it as one group, which is the exact opposite of the truth the
 * A/B split exists to show.
 */
const ANCHOR_KINDS: readonly NodeKind[] = ['busway', 'switchgear', 'dcbus', 'rpp'];

/** How confident we are that this grouping is real. Strongest wins, as everywhere else. */
export type GroupProvenance = 'recorded' | 'declared' | 'inferred';

export interface PlantGroup {
  /** Stable across reloads: derived from the anchor's node id, which the document owns. */
  readonly id: string;
  /** What an operator sees in the filter — "Cluster 1", "Train A". */
  readonly label: string;
  /** The combining node this group hangs off. Removing it dissolves the group. */
  readonly anchorNodeId: string;
  /** Every node upstream of the anchor, plus the anchor. Sorted, so the id is stable. */
  readonly nodeIds: readonly string[];
  /** Devices reachable through those nodes, by `deviceId` AND through `measuredBy`. */
  readonly deviceIds: readonly number[];
  readonly provenance: GroupProvenance;
  /** The audit trail. A group that cannot say why it exists is a guess wearing a label. */
  readonly why: string;
}

export interface PlantGrouping {
  readonly groups: readonly PlantGroup[];
  /**
   * Nodes that belong to NO group — either serving several (the utility feed, the service
   * meter) or hanging off none at all. Shown as ungrouped, never hidden and never forced
   * into a bucket: an invented grouping is the same defect as invented equipment.
   */
  readonly ungroupedNodeIds: readonly string[];
  readonly ungroupedDeviceIds: readonly number[];
}

export interface GroupingSpec {
  /** Singular, as an operator of THIS kind of plant would say it. */
  readonly noun: string;
  readonly plural: string;
  readonly why: string;
}

/**
 * A generation SITE divides into PLANTS: a set of arrays and inverters landing on one bus,
 * with its own meter and its own connection to the grid. Jamshoro has five.
 *
 * The operator's word is "plant", not "cluster" — the as-built drawing labels them C-1…C-5
 * and the vendor portal embeds the same tokens in device names, but neither is what the
 * people running the site say. The noun is theirs; only the mechanism is ours.
 */
const CLUSTER: GroupingSpec = {
  noun: 'Plant',
  plural: 'Plants',
  why: 'A generation site divides into plants that each combine their inverters on their own bus and meter separately.',
};

/**
 * An ICT plant divides into distribution trains — the redundant paths whose independence IS
 * the compliance question. JAZZMSC5 is two mirrored UPS/rectifier trains onto a shared PDU.
 */
const TRAIN: GroupingSpec = {
  noun: 'Train',
  plural: 'Trains',
  why: 'A plant built for uptime divides into redundant distribution paths, and their independence is the verdict.',
};

/**
 * Everything else. Neutral on purpose: a hospital's isolated-power panels and a refinery's
 * hazardous-area divisions are real groupings with real names, and NEITHER has been
 * designed. Borrowing "Cluster" or "Train" would put a solar word on a hospital, which is
 * the same mistake as handing it a rectifier card.
 */
const PART: GroupingSpec = {
  noun: 'Group',
  plural: 'Groups',
  why: 'No grouping vocabulary designed for this plant type yet — using a neutral word rather than borrowing another type’s.',
};

/** EXHAUSTIVE, like `PLANT_VIEWS` and `TEMPLATES`. A new archetype is a type error here. */
export const GROUPING: Record<ArchetypeId, GroupingSpec> = {
  datacenter: TRAIN,
  cellsite: TRAIN,
  centraloffice: TRAIN,
  edge5g: TRAIN,
  fmps: TRAIN,
  renewable: CLUSTER,
  storage: CLUSTER,
  healthcare: PART,
  industrial: PART,
  hazardous: PART,
  transport: PART,
  commercial: PART,
  evcharging: PART,
};

/** What this plant type calls its parts. Unknown archetype falls back to the neutral word. */
export function groupingFor(archetype: string | null | undefined): GroupingSpec {
  if (!archetype) return PART;
  return GROUPING[archetype as ArchetypeId] ?? PART;
}

/**
 * Every device a node can speak for: its own, plus any it reads through.
 *
 * EXPORTED, because `app/plant-context.tsx` now needs the same answer for a different reason.
 * The plants themselves are table rows since migration 0048, so the context maps a plant's
 * recorded DEVICES back onto the nodes that speak for them — and "which devices does this node
 * speak for" has exactly one correct answer, which is this one. A second copy there would be
 * free to forget `measuredBy`, and a node that reads through a controller would then fall
 * outside its own plant.
 */
export function devicesOf(n: TopologyNode): number[] {
  const out: number[] = [];
  if (n.deviceId !== null) out.push(n.deviceId);
  for (const b of n.measuredBy ?? []) out.push(b.deviceId);
  return out;
}

/**
 * The parts of one plant, from its topology.
 *
 * Walks UPSTREAM from every anchor. A node reachable from exactly one anchor joins that
 * group; a node reachable from several serves several and joins none (rule 3); a node
 * reachable from none is ungrouped.
 *
 * Deliberately takes the node list rather than a `Topology`, so a test can state a graph in
 * a few lines and so a caller with a partially-loaded document can still ask.
 */
export function derivePlantGroups(
  nodes: readonly TopologyNode[],
  archetype: string | null | undefined,
): PlantGrouping {
  const spec = groupingFor(archetype);

  /* `feeds` lives on the SOURCE, so upstream needs the reverse index built once. */
  const feeders = new Map<string, string[]>();
  for (const n of nodes) {
    for (const t of n.feeds) {
      const list = feeders.get(t);
      if (list) list.push(n.id);
      else feeders.set(t, [n.id]);
    }
  }

  const candidates = nodes.filter((n) => (ANCHOR_KINDS as readonly string[]).includes(n.kind));
  const candidateIds = new Set(candidates.map((n) => n.id));

  /**
   * AN ANCHOR THAT OTHER ANCHORS FEED IS WHERE PLANTS COMBINE, NOT A PLANT.
   *
   * This module was written against a flat fleet: every anchor sat at the top of its own
   * island, so no node could ever reach two of them and rule 3 below — reachable from
   * several, therefore serving several, therefore joining none — only ever fired on a
   * genuinely shared component.
   *
   * Jamshoro's topology v6 broke that assumption in one edit. Connecting the five plant
   * buses to a main LV board (kind `switchgear`, and therefore an anchor) put a SECOND
   * anchor downstream of every inverter and every array. Rule 3 then did exactly what it
   * says and unassigned all of them: six groups, every one reporting zero devices, and the
   * plant filter inert across the whole console. Nothing was hidden — `plantAdmits` admits
   * everything when no group claims anything, which is the safe direction — but the feature
   * was useless, and a per-plant table elsewhere rendered six rows of "0 kW · 0 inverters"
   * on a site generating 388 kW.
   *
   * The rule was not wrong, it was incomplete: it had no notion of HIERARCHY. A plant is a
   * block of equipment that combines on its own bus; the board those buses then combine on
   * is a level above, and calling it a sixth plant is a category error. So an anchor with
   * another anchor upstream of it is excluded, which restores rule 3's precondition — each
   * node reaches at most one remaining anchor — rather than weakening rule 3 itself.
   *
   * Costs nothing on a flat document: with no anchor above another this filter removes
   * nothing, so every site that worked before behaves identically. Both live sites are
   * pinned as fixtures below.
   */
  const combinesOtherAnchors = (id: string): boolean => {
    const seen = new Set<string>([id]);
    const queue = [id];
    while (queue.length > 0) {
      const cur = queue.pop() as string;
      for (const up of feeders.get(cur) ?? []) {
        if (seen.has(up)) continue;
        if (candidateIds.has(up)) return true;
        seen.add(up);
        queue.push(up);
      }
    }
    return false;
  };

  const anchors = candidates
    .filter((n) => !combinesOtherAnchors(n.id))
    .sort((a, b) => a.x - b.x || a.id.localeCompare(b.id));

  /* Which anchors each node sits upstream of. Two entries means it serves both. */
  const reach = new Map<string, Set<string>>();
  for (const anchor of anchors) {
    const seen = new Set<string>([anchor.id]);
    const queue = [anchor.id];
    while (queue.length > 0) {
      const id = queue.pop() as string;
      let set = reach.get(id);
      if (!set) {
        set = new Set<string>();
        reach.set(id, set);
      }
      set.add(anchor.id);
      for (const up of feeders.get(id) ?? []) {
        if (seen.has(up)) continue;
        seen.add(up);
        queue.push(up);
      }
    }
  }

  const groups: PlantGroup[] = [];
  anchors.forEach((anchor, i) => {
    const members = nodes.filter((n) => {
      const set = reach.get(n.id);
      return set !== undefined && set.size === 1 && set.has(anchor.id);
    });
    const nodeIds = members.map((n) => n.id).sort();
    const deviceIds = [...new Set(members.flatMap(devicesOf))].sort((a, b) => a - b);

    /* A group is only as certain as its least certain member: if any node in it was
       inferred from telemetry rather than drawn or recorded, the grouping is inferred too. */
    const anyInferred = members.some((n) => n.inferred === true);
    const sources = members.filter((n) => (feeders.get(n.id) ?? []).length === 0).length;

    groups.push({
      id: `g:${anchor.id}`,
      label: `${spec.noun} ${i + 1}`,
      anchorNodeId: anchor.id,
      nodeIds,
      deviceIds,
      provenance: anyInferred ? 'inferred' : 'recorded',
      why:
        `${members.length} node(s) combine on ${anchor.name || anchor.id}` +
        (sources > 0 ? `, fed by ${sources} source(s)` : '') +
        (anyInferred ? '; some were inferred from telemetry rather than drawn' : ''),
    });
  });

  const grouped = new Set(groups.flatMap((g) => g.nodeIds));
  const ungrouped = nodes.filter((n) => !grouped.has(n.id));

  return {
    groups,
    ungroupedNodeIds: ungrouped.map((n) => n.id).sort(),
    ungroupedDeviceIds: [...new Set(ungrouped.flatMap(devicesOf))].sort((a, b) => a - b),
  };
}

/**
 * Whether the console should offer a grouping filter at all.
 *
 * DERIVED, never configured — the same call the industry switcher makes. One part is not a
 * division of anything, so a single-cluster site gets no control and no explaining to do;
 * a control that cannot narrow anything is hidden, not disabled.
 */
export function showsGroupFilter(g: PlantGrouping): boolean {
  return g.groups.length >= 2;
}

/** Which group a device is in, or null when it serves the whole site or none of it. */
export function groupOfDevice(g: PlantGrouping, deviceId: number): PlantGroup | null {
  return g.groups.find((x) => x.deviceIds.includes(deviceId)) ?? null;
}


/** Exported so a reader can see the anchor set is a short, reasoned list rather than implied. */
export const GROUP_ANCHOR_KINDS = ANCHOR_KINDS;
export const ALL_ARCHETYPES = ARCHETYPE_IDS;
